/**
 * 連続タイムグリッド
 *
 * 複数日を日付で区切らず、ひとつながりの時間軸として縦に並べて描画する。
 * FullCalendar は1日単位のビューしか持たないため、ここは自前で組む。
 */

import './calendar-event.css'

import { addDays, format } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { formatRelativeDate } from '@/lib/format-utils'
import { cn } from '@/lib/utils'

import type { ActivityCalendarEvent, CalendarEventProps } from './calendar-event-builder'

/** 1時間あたりの高さ(px)。FullCalendar 側の 10 分スロット(1.5rem) × 6 に合わせる */
const HOUR_HEIGHT = 144
const PX_PER_MINUTE = HOUR_HEIGHT / 60
const DAY_HEIGHT = HOUR_HEIGHT * 24
/** 時刻ラベルを置く左端の幅(px) */
const GUTTER_WIDTH = 50
/** 短いイベントでも文字が読める高さを確保する */
const MIN_EVENT_HEIGHT = 16
/** 画面外も少し先まで描画しておく余白(px) */
const RENDER_BUFFER = DAY_HEIGHT
/** この位置より上までスクロールしたら過去を読み込む */
const LOAD_MORE_THRESHOLD = 400
/** 現在時刻が表示範囲にないときに最初に見せる時刻 */
const DEFAULT_START_HOUR = 6
/** 1時間ごとの罫線の色 */
const GRID_LINE_COLOR = 'color-mix(in oklab, var(--border) 60%, transparent)'

type Props = {
  /** 表示範囲の先頭（その日の 00:00） */
  rangeStart: Date
  /** 表示する日数 */
  dayCount: number
  events: ActivityCalendarEvent[]
  onEventSelect: (props: CalendarEventProps) => void
  /** 上端付近までスクロールしたときに過去を読み込む（単日表示では渡さない） */
  onLoadMorePast?: () => void
  isLoadingMore?: boolean
  /** この日(YYYY-MM-DD)の先頭までスクロールする */
  scrollToDate?: string | null
  /** スクロールし終わったときに呼ぶ */
  onScrolledToDate?: () => void
}

type PositionedEvent = {
  event: ActivityCalendarEvent
  column: number
  columns: number
}

// 重なり合うイベントを横に並べるため、同時に存在するものを塊にして列を割り当てる
const layoutEvents = (events: ActivityCalendarEvent[]): PositionedEvent[] => {
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime())
  const result: PositionedEvent[] = []

  let cluster: ActivityCalendarEvent[] = []
  let clusterEnd = 0

  const flush = () => {
    if (cluster.length === 0) return

    // columnEnds[i] = i列目に最後に置いたイベントの終了時刻
    const columnEnds: number[] = []
    const assigned: number[] = []

    cluster.forEach((event) => {
      const startMs = event.start.getTime()
      const endMs = event.end.getTime()
      const free = columnEnds.findIndex((end) => end <= startMs)
      if (free === -1) {
        columnEnds.push(endMs)
        assigned.push(columnEnds.length - 1)
        return
      }
      columnEnds.splice(free, 1, endMs)
      assigned.push(free)
    })

    cluster.forEach((event, i) => {
      result.push({ event, column: assigned.at(i) ?? 0, columns: columnEnds.length })
    })

    cluster = []
    clusterEnd = 0
  }

  for (const event of sorted) {
    if (cluster.length > 0 && event.start.getTime() >= clusterEnd) flush()
    cluster.push(event)
    clusterEnd = Math.max(clusterEnd, event.end.getTime())
  }
  flush()

  return result
}

export const ContinuousCalendarGrid = ({
  rangeStart,
  dayCount,
  events,
  onEventSelect,
  onLoadMorePast,
  isLoadingMore = false,
  scrollToDate = null,
  onScrolledToDate,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState({ top: 0, bottom: 0 })
  const [now, setNow] = useState(() => new Date())

  const totalHeight = dayCount * DAY_HEIGHT
  const rangeStartMs = rangeStart.getTime()

  const toOffset = useCallback(
    (date: Date) => ((date.getTime() - rangeStartMs) / 60000) * PX_PER_MINUTE,
    [rangeStartMs],
  )

  const positioned = useMemo(() => layoutEvents(events), [events])

  const updateViewport = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setViewport({ top: el.scrollTop, bottom: el.scrollTop + el.clientHeight })
  }, [])

  const handleScroll = useCallback(() => {
    updateViewport()
    const el = scrollRef.current
    if (el && el.scrollTop < LOAD_MORE_THRESHOLD) {
      onLoadMorePast?.()
    }
  }, [updateViewport, onLoadMorePast])

  // 過去を読み込んで上に日が足された分、見ている位置がずれないようスクロール量を補正する
  const prevRangeStartRef = useRef(rangeStartMs)
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && rangeStartMs < prevRangeStartRef.current) {
      el.scrollTop += ((prevRangeStartRef.current - rangeStartMs) / 60000) * PX_PER_MINUTE
    }
    prevRangeStartRef.current = rangeStartMs
  }, [rangeStartMs])

  // 初回描画時は現在時刻が見える位置から始める
  const didInitScroll = useRef(false)
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || dayCount === 0 || didInitScroll.current) return
    didInitScroll.current = true
    const nowOffset = toOffset(new Date())
    // 過去日だけを見ているときは現在時刻が範囲外なので、朝から表示する
    const anchor =
      nowOffset >= 0 && nowOffset <= dayCount * DAY_HEIGHT
        ? nowOffset - el.clientHeight * 0.6
        : DEFAULT_START_HOUR * HOUR_HEIGHT
    el.scrollTop = Math.max(0, anchor)
    updateViewport()
  }, [dayCount, toOffset, updateViewport])

  // 現在時刻の線を1分ごとに動かす
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000)
    return () => clearInterval(timer)
  }, [])

  // ヘッダで選ばれた日へ移動する
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !scrollToDate) return

    const offset = toOffset(new Date(`${scrollToDate}T00:00:00`))
    // 日が追加された直後は高さが確定していないため、レイアウト後に動かす
    const frameId = requestAnimationFrame(() => {
      el.scrollTop = Math.max(0, Math.min(offset, el.scrollHeight - el.clientHeight))
      updateViewport()
      onScrolledToDate?.()
    })
    return () => cancelAnimationFrame(frameId)
  }, [scrollToDate, toOffset, updateViewport, onScrolledToDate])

  // 画面に入っている範囲だけ時刻ラベルと日付の区切りを描く
  const visibleHours = useMemo(() => {
    const from = Math.max(0, Math.floor((viewport.top - RENDER_BUFFER) / HOUR_HEIGHT))
    const to = Math.min(dayCount * 24, Math.ceil((viewport.bottom + RENDER_BUFFER) / HOUR_HEIGHT))
    return Array.from({ length: Math.max(0, to - from) }, (_, i) => from + i)
  }, [viewport, dayCount])

  const visibleDays = useMemo(() => {
    const from = Math.max(0, Math.floor((viewport.top - RENDER_BUFFER) / DAY_HEIGHT))
    const to = Math.min(dayCount, Math.ceil((viewport.bottom + RENDER_BUFFER) / DAY_HEIGHT))
    return Array.from({ length: Math.max(0, to - from) }, (_, i) => from + i)
  }, [viewport, dayCount])

  // ヘッダーに出す「いま見ている日」
  const headerDate = useMemo(() => {
    const index = Math.min(dayCount - 1, Math.max(0, Math.floor(viewport.top / DAY_HEIGHT)))
    return addDays(rangeStart, index)
  }, [viewport.top, dayCount, rangeStart])

  const nowOffset = toOffset(now)
  const isNowVisible = nowOffset >= 0 && nowOffset <= totalHeight

  return (
    <div className="flex h-full flex-col">
      {dayCount > 1 && (
        <div className="bg-background/95 flex items-center gap-2 border-b px-4 py-2 backdrop-blur-sm">
          <span className="text-sm font-medium">{formatRelativeDate(format(headerDate, 'yyyy-MM-dd'))}</span>
          {isLoadingMore && <Loader2 className="text-muted-foreground size-4 animate-spin" />}
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-y-auto"
        // 上に日を足したときの位置補正は自前でやるため、ブラウザのアンカリングを切る
        style={{ overflowAnchor: 'none' }}
      >
        <div
          className="relative"
          style={{
            height: totalHeight,
            // 1時間ごとの罫線は DOM を増やさず背景で引く
            // --border は oklch なので hsl() では包めない
            backgroundImage: `repeating-linear-gradient(to bottom, ${GRID_LINE_COLOR} 0px, ${GRID_LINE_COLOR} 1px, transparent 1px, transparent ${HOUR_HEIGHT}px)`,
          }}
        >
          {visibleHours.map((hourIndex) => {
            const hour = hourIndex % 24
            // 0:00 は日付の区切り行が兼ねる
            if (hour === 0 && dayCount > 1) return null
            return (
              <div
                key={hourIndex}
                className="text-muted-foreground absolute text-[0.7rem] font-medium"
                style={{ top: hourIndex * HOUR_HEIGHT + 2, left: 0, width: GUTTER_WIDTH - 8, textAlign: 'right' }}
              >
                {hour.toString().padStart(2, '0')}:00
              </div>
            )
          })}

          {dayCount > 1 &&
            visibleDays.map((dayIndex) => (
              <div
                key={dayIndex}
                className="border-border/70 absolute inset-x-0 flex items-center gap-2 border-t-2 pt-0.5 pl-1"
                style={{ top: dayIndex * DAY_HEIGHT }}
              >
                <span className="bg-background/90 text-muted-foreground rounded px-1.5 py-0.5 text-[0.7rem] font-semibold">
                  {formatRelativeDate(format(addDays(rangeStart, dayIndex), 'yyyy-MM-dd'))}
                </span>
              </div>
            ))}

          {positioned.length > 0 && (
            <div className="absolute inset-y-0" style={{ left: GUTTER_WIDTH, right: 4 }}>
              {positioned.map(({ event, column, columns }) => {
                const top = toOffset(event.start)
                const height = Math.max(MIN_EVENT_HEIGHT, toOffset(event.end) - top)
                const widthPct = 100 / columns
                const lines = event.title.split('\n')

                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => onEventSelect(event.props)}
                    className={cn(
                      // button は中身が縦中央に寄るため、flex で明示的に上寄せにする
                      'absolute flex flex-col items-stretch justify-start overflow-hidden rounded-md border-l-[3px] px-1.5! py-0.5! text-left text-white shadow-sm transition-[filter] hover:brightness-110',
                      event.className,
                    )}
                    style={{
                      top,
                      height,
                      left: `${column * widthPct}%`,
                      width: `calc(${widthPct}% - 2px)`,
                    }}
                  >
                    <div className="wrap-break-word text-xs leading-snug font-semibold">{lines[0]}</div>
                    {lines.slice(1).map((line, i) => (
                      <div key={i} className="wrap-break-word text-[10px] leading-snug opacity-80">
                        {line}
                      </div>
                    ))}
                  </button>
                )
              })}
            </div>
          )}

          {isNowVisible && (
            <div
              className="border-destructive pointer-events-none absolute inset-x-0 z-10 border-t-2"
              style={{ top: nowOffset }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
